import { ContentType } from '@standardnotes/domain-core';
import { removeFromArray, Uuids } from '@standardnotes/utils';
import { DeltaFileImport, isDeletedPayload, ImmutablePayloadCollection, PayloadSource, PayloadCollection, PayloadEmitSource, DeletedPayload, isEncryptedPayload, isDecryptedPayload, getIncrementedDirtyIndex, } from '@standardnotes/models';
import { AbstractService, } from '@standardnotes/services';
/**
 * The payload manager is responsible for keeping state regarding what items exist in the
 * global application state. It does so by exposing functions that allow consumers to 'map'
 * a detached payload into global application state. Whenever a change is made or retrieved
 * from any source, it must be mapped in order to be properly reflected in global application state.
 * The model manager deals only with in-memory state, and does not deal directly with storage.
 * It also serves as a query store, and can be queried for current notes, tags, etc.
 * It exposes methods that allow consumers to listen to mapping events. This is how
 * applications 'stream' items to display in the interface.
 */
export class PayloadManager extends AbstractService {
    constructor(logger, internalEventBus) {
        super(internalEventBus);
        this.logger = logger;
        this.internalEventBus = internalEventBus;
        this.changeObservers = [];
        this.emitQueue = [];
        this.collection = new PayloadCollection();
    }
    /**
     * Our payload collection keeps the latest mapped payload for every payload
     * that passes through our mapping function. Use this to query current state
     * as needed to make decisions, like about duplication or uuid alteration.
     */
    getMasterCollection() {
        return ImmutablePayloadCollection.FromCollection(this.collection);
    }
    deinit() {
        super.deinit();
        this.changeObservers.length = 0;
        this.resetState();
    }
    resetState() {
        this.collection = new PayloadCollection();
    }
    find(uuids) {
        return this.collection.findAll(uuids);
    }
    findOne(uuid) {
        return this.collection.findAll([uuid])[0];
    }
    all(contentType) {
        return this.collection.all(contentType);
    }
    get integrityPayloads() {
        return this.collection.integrityPayloads();
    }
    get nonDeletedItems() {
        return this.collection.nondeletedElements();
    }
    get invalidPayloads() {
        return this.collection.invalidElements();
    }
    async emitDeltaEmit(emit, sourceKey) {
        var _a;
        if (emit.emits.length === 0 && ((_a = emit.ignored) === null || _a === void 0 ? void 0 : _a.length) === 0) {
            return [];
        }
        return new Promise((resolve) => {
            const element = {
                emit: emit,
                sourceKey,
                resolve,
            };
            this.emitQueue.push(element);
            if (this.emitQueue.length === 1) {
                void this.popQueue();
            }
        });
    }
    /**
     * One of many mapping helpers available.
     * This function maps a payload to an item
     * @returns every paylod altered as a result of this operation, to be
     * saved to storage by the caller
     */
    async emitPayload(payload, source, sourceKey) {
        return this.emitPayloads([payload], source, sourceKey);
    }
    /**
     * This function maps multiple payloads to items, and is the authoratative mapping
     * function that all other mapping helpers rely on
     * @returns every paylod altered as a result of this operation, to be
     * saved to storage by the caller
     */
    async emitPayloads(payloads, source, sourceKey) {
        const emit = {
            emits: payloads,
            source: source,
        };
        return this.emitDeltaEmit(emit, sourceKey);
    }
    popQueue() {
        const first = this.emitQueue[0];
        const { changed, inserted, discarded, unerrored } = this.applyPayloads(first.emit.emits);
        this.notifyChangeObservers(changed, inserted, discarded, first.emit.ignored || [], unerrored, first.emit.source, first.sourceKey);
        removeFromArray(this.emitQueue, first);
        first.resolve([...changed, ...inserted, ...discarded]);
        if (this.emitQueue.length > 0) {
            void this.popQueue();
        }
    }
    applyPayloads(applyPayloads) {
        const changed = [];
        const inserted = [];
        const discarded = [];
        const unerrored = [];
        for (const apply of applyPayloads) {
            if (!apply.uuid || !apply.content_type) {
                console.error('Payload is corrupt', apply);
                continue;
            }
            this.logger.debug('applying payload', apply.uuid, 'globalDirtyIndexAtLastSync', apply.globalDirtyIndexAtLastSync, 'dirtyIndex', apply.dirtyIndex, 'dirty', apply.dirty);
            const base = this.collection.find(apply.uuid);
            if (isDeletedPayload(apply) && apply.discardable) {
                this.collection.discard(apply);
                discarded.push(apply);
            }
            else {
                this.collection.set(apply);
                if (base) {
                    changed.push(apply);
                    if (isEncryptedPayload(base) && isDecryptedPayload(apply)) {
                        unerrored.push(apply);
                    }
                }
                else {
                    inserted.push(apply);
                }
            }
        }
        return { changed, inserted, discarded, unerrored };
    }
    /**
     * Notifies observers when an item has been mapped.
     * @param types - An array of content types to listen for
     * @param priority - The lower the priority, the earlier the function is called
     *  wrt to other observers
     */
    addObserver(types, callback, priority = 1) {
        if (!Array.isArray(types)) {
            types = [types];
        }
        const observer = {
            types,
            priority,
            callback,
        };
        this.changeObservers.push(observer);
        const thislessChangeObservers = this.changeObservers;
        return () => {
            removeFromArray(thislessChangeObservers, observer);
        };
    }
    /**
     * This function is mostly for internal use, but can be used externally by consumers who
     * explicitely understand what they are doing (want to propagate model state without mapping)
     */
    notifyChangeObservers(changed, inserted, discarded, ignored, unerrored, source, sourceKey) {
        /** Slice the observers array as sort modifies in-place */
        const observers = this.changeObservers.slice().sort((a, b) => {
            return a.priority < b.priority ? -1 : 1;
        });
        const filter = (payloads, types) => {
            return types.includes(ContentType.TYPES.Any)
                ? payloads.slice()
                : payloads.slice().filter((payload) => {
                    return types.includes(payload.content_type);
                });
        };
        for (const observer of observers) {
            observer.callback({
                changed: filter(changed, observer.types),
                inserted: filter(inserted, observer.types),
                discarded: filter(discarded, observer.types),
                ignored: filter(ignored, observer.types),
                unerrored: filter(unerrored, observer.types),
                source,
                sourceKey,
            });
        }
    }
    /**
     * Imports an array of payloads from an external source (such as a backup file)
     * and marks the items as dirty.
     */
    async importPayloads(payloads, historyMap) {
        const sourcedPayloads = payloads.map((p) => p.copy(undefined, PayloadSource.FileImport));
        const delta = new DeltaFileImport(this.getMasterCollection(), sourcedPayloads, historyMap);
        const emit = delta.result();
        await this.emitDeltaEmit(emit);
        return Uuids(payloads);
    }
    removePayloadLocally(payload) {
        this.collection.discard(payload);
    }
    erroredPayloadsForContentType(contentType) {
        return this.collection.invalidElements().filter((p) => p.content_type === contentType);
    }
    async deleteErroredPayloads(payloads) {
        const deleted = payloads.map((payload) => new DeletedPayload({
            ...payload.ejected(),
            deleted: true,
            content: undefined,
            dirty: true,
            dirtyIndex: getIncrementedDirtyIndex(),
        }, payload.source));
        await this.emitPayloads(deleted, PayloadEmitSource.LocalChanged);
    }
    getDiagnostics() {
        return Promise.resolve({
            payloads: {
                integrityPayloads: this.integrityPayloads,
                nonDeletedItemCount: this.nonDeletedItems.length,
                invalidPayloadsCount: this.invalidPayloads.length,
            },
        });
    }
}
